/**
 * EmailComposeModal.jsx — Modal de composition d'email avec pièces jointes
 * Utilisable depuis n'importe quelle vue conseiller/intervenant/admin.
 *
 * Usage :
 *   <EmailComposeModal
 *     isOpen={showEmailModal}
 *     onClose={() => setShowEmailModal(false)}
 *     defaultTo="jean.dupont@email.fr"
 *     defaultSubject="Votre CV"
 *     beneficiaireId="abc123"   // optionnel — si fourni, utilise la route /send-document
 *   />
 */

import React, { useState, useRef, useEffect } from "react";
import { emailComposeService } from "../../services/api";

const MAX_FILE_SIZE_MB = 10;

// Libellés français des rôles pour l'annuaire des destinataires
const ROLE_LABELS = {
  admin: "Administrateurs",
  referent: "Référents",
  animateur: "Intervenants",
  financeur: "Financeurs",
  "bénéficiaire": "Bénéficiaires",
  apprenant: "Bénéficiaires",
};
const ROLE_ORDER = ["admin", "referent", "animateur", "financeur", "bénéficiaire", "apprenant"];

export default function EmailComposeModal({
  isOpen,
  onClose,
  defaultTo = "",
  defaultSubject = "",
  defaultBody = "",
  beneficiaireId = null,
}) {
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // Annuaire des utilisateurs de l'application (envoi groupé)
  const [annuaire, setAnnuaire] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // objets {id, nom, prenom, email, role}
  const [showAnnuaire, setShowAnnuaire] = useState(false);
  const [searchAnnuaire, setSearchAnnuaire] = useState("");
  const [sendProgress, setSendProgress] = useState(null); // {current, total}

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    emailComposeService
      .getAnnuaire()
      .then((data) => { if (!cancelled) setAnnuaire(data || []); })
      .catch(() => {}); // annuaire indisponible → saisie manuelle toujours possible
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleUser = (user) => {
    setSelectedUsers((prev) =>
      prev.some((u) => u.id === user.id)
        ? prev.filter((u) => u.id !== user.id)
        : [...prev, user]
    );
  };

  const toggleGroup = (usersOfGroup) => {
    const allSelected = usersOfGroup.every((u) => selectedUsers.some((s) => s.id === u.id));
    setSelectedUsers((prev) => {
      const without = prev.filter((p) => !usersOfGroup.some((u) => u.id === p.id));
      return allSelected ? without : [...without, ...usersOfGroup];
    });
  };

  // Annuaire filtré par recherche puis groupé par rôle
  const filteredAnnuaire = annuaire.filter((u) => {
    const q = searchAnnuaire.toLowerCase();
    return !q || `${u.prenom} ${u.nom} ${u.email}`.toLowerCase().includes(q);
  });
  const groupes = ROLE_ORDER
    .map((role) => ({
      role,
      label: ROLE_LABELS[role] || role,
      users: filteredAnnuaire.filter((u) => u.role === role),
    }))
    .filter((g) => g.users.length > 0);

  // Fusionne les groupes portant le même libellé (bénéficiaire/apprenant)
  const groupesFusionnes = Object.values(
    groupes.reduce((acc, g) => {
      if (acc[g.label]) acc[g.label].users.push(...g.users);
      else acc[g.label] = { ...g, users: [...g.users] };
      return acc;
    }, {})
  );

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    const oversized = selected.filter(
      (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024
    );
    if (oversized.length) {
      setError(
        `Fichier(s) trop volumineux : ${oversized.map((f) => f.name).join(", ")} (max ${MAX_FILE_SIZE_MB} Mo)`
      );
      return;
    }
    setError(null);
    setFiles((prev) => [...prev, ...selected]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    // Destinataires = utilisateurs cochés dans l'annuaire + emails saisis manuellement
    const manualEmails = to
      .split(/[,;]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    const allEmails = [
      ...selectedUsers.map((u) => u.email),
      ...manualEmails,
    ].filter((email, i, arr) => arr.indexOf(email) === i); // dédoublonnage

    if (allEmails.length === 0 || !subject || !body) {
      setError("Au moins un destinataire, l'objet et le message sont obligatoires.");
      return;
    }

    setSending(true);
    setError(null);

    try {
      const bodyHtml = body.replace(/\n/g, "<br/>");

      if (beneficiaireId && files.length === 1 && allEmails.length === 1) {
        // Route spécialisée bénéficiaire (document unique)
        await emailComposeService.sendDocumentToBeneficiaire({
          beneficiaireId,
          documentType: "document",
          subject,
          bodyHtml,
          file: files[0],
        });
      } else {
        // Envoi individuel à chaque destinataire (les adresses ne sont pas
        // divulguées entre destinataires — conforme RGPD)
        const failed = [];
        for (let i = 0; i < allEmails.length; i++) {
          setSendProgress({ current: i + 1, total: allEmails.length });
          try {
            await emailComposeService.sendEmail({
              toEmail: allEmails[i],
              subject,
              bodyHtml,
              attachments: files,
            });
          } catch (err) {
            failed.push(allEmails[i]);
          }
        }
        setSendProgress(null);
        if (failed.length > 0) {
          setError(
            `${allEmails.length - failed.length}/${allEmails.length} email(s) envoyé(s). Échec pour : ${failed.join(", ")}`
          );
          setSending(false);
          return;
        }
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setSelectedUsers([]);
        onClose();
      }, 1500);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Erreur lors de l'envoi. Vérifiez votre connexion."
      );
    } finally {
      setSendProgress(null);
      setSending(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          width: "min(600px, 95vw)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#111827" }}>
            ✉️ Envoyer un email
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 20,
              color: "#6B7280",
              padding: 4,
            }}
          >
            ×
          </button>
        </div>

        {/* Corps du formulaire */}
        <div style={{ padding: "20px", flex: 1, overflowY: "auto" }}>
          {/* Destinataires */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>À *</label>
              <button
                type="button"
                onClick={() => setShowAnnuaire(!showAnnuaire)}
                style={{
                  background: showAnnuaire ? "#EFF6FF" : "none",
                  border: "1px solid #BFDBFE",
                  borderRadius: 8,
                  cursor: "pointer",
                  color: "#2563EB",
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "4px 10px",
                }}
              >
                👥 {showAnnuaire ? "Fermer l'annuaire" : "Choisir dans l'annuaire"}
                {selectedUsers.length > 0 ? ` (${selectedUsers.length})` : ""}
              </button>
            </div>

            {/* Destinataires sélectionnés (puces) */}
            {selectedUsers.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {selectedUsers.map((u) => (
                  <span
                    key={u.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: "#EFF6FF",
                      border: "1px solid #BFDBFE",
                      color: "#1D4ED8",
                      borderRadius: 999,
                      padding: "3px 10px",
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {u.prenom} {u.nom}
                    <button
                      type="button"
                      onClick={() => toggleUser(u)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#1D4ED8", fontWeight: "bold", padding: 0 }}
                      aria-label={`Retirer ${u.prenom} ${u.nom}`}
                    >
                      ✕
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => setSelectedUsers([])}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: 12, textDecoration: "underline" }}
                >
                  Tout retirer
                </button>
              </div>
            )}

            {/* Panneau annuaire */}
            {showAnnuaire && (
              <div
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  marginBottom: 8,
                  maxHeight: 260,
                  overflowY: "auto",
                  background: "#FAFAFA",
                }}
              >
                <div style={{ padding: 8, position: "sticky", top: 0, background: "#FAFAFA", borderBottom: "1px solid #E5E7EB" }}>
                  <input
                    type="text"
                    value={searchAnnuaire}
                    onChange={(e) => setSearchAnnuaire(e.target.value)}
                    placeholder="🔍 Rechercher un nom ou un email..."
                    style={{ ...inputStyle, padding: "8px 10px", fontSize: 13 }}
                  />
                </div>
                {groupesFusionnes.length === 0 ? (
                  <p style={{ padding: 12, fontSize: 13, color: "#6B7280", margin: 0 }}>
                    {annuaire.length === 0 ? "Chargement de l'annuaire..." : "Aucun résultat"}
                  </p>
                ) : (
                  groupesFusionnes.map((groupe) => {
                    const allSelected = groupe.users.every((u) => selectedUsers.some((s) => s.id === u.id));
                    return (
                      <div key={groupe.label}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 12px",
                            background: "#F3F4F6",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#374151",
                            textTransform: "uppercase",
                            letterSpacing: "0.03em",
                          }}
                        >
                          <span>{groupe.label} ({groupe.users.length})</span>
                          <button
                            type="button"
                            onClick={() => toggleGroup(groupe.users)}
                            style={{ background: "none", border: "none", cursor: "pointer", color: "#2563EB", fontSize: 12, fontWeight: 500 }}
                          >
                            {allSelected ? "Tout décocher" : "Tout sélectionner"}
                          </button>
                        </div>
                        {groupe.users.map((u) => {
                          const checked = selectedUsers.some((s) => s.id === u.id);
                          return (
                            <label
                              key={u.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "7px 12px",
                                cursor: "pointer",
                                background: checked ? "#EFF6FF" : "transparent",
                                borderBottom: "1px solid #F3F4F6",
                                fontSize: 13,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleUser(u)}
                                style={{ accentColor: "#2563EB", width: 15, height: 15 }}
                              />
                              <span style={{ fontWeight: 500, color: "#111827" }}>
                                {u.prenom} {u.nom}
                              </span>
                              <span style={{ color: "#6B7280", fontSize: 12, marginLeft: "auto" }}>
                                {u.email}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Saisie manuelle (destinataires externes) */}
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={selectedUsers.length > 0 ? "Autres emails (optionnel, séparés par des virgules)" : "email@exemple.fr — ou choisissez dans l'annuaire"}
              style={inputStyle}
            />
          </div>

          {/* Objet */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Objet *</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet de l'email"
              style={inputStyle}
            />
          </div>

          {/* Corps */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Message *</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Votre message..."
              rows={6}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
            />
          </div>

          {/* Pièces jointes */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Pièces jointes (max 10 Mo chacune)</label>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: "2px dashed #D1D5DB",
                borderRadius: 8,
                padding: "14px",
                cursor: "pointer",
                textAlign: "center",
                color: "#6B7280",
                fontSize: 14,
                transition: "border-color 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.borderColor = "#2563EB")}
              onMouseOut={(e) => (e.currentTarget.style.borderColor = "#D1D5DB")}
            >
              📎 Cliquer pour ajouter un fichier (PDF, Word, image...)
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />

            {/* Liste des fichiers sélectionnés */}
            {files.map((file, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 10px",
                  background: "#F3F4F6",
                  borderRadius: 6,
                  marginTop: 6,
                  fontSize: 13,
                }}
              >
                <span>📄 {file.name} ({(file.size / 1024).toFixed(0)} Ko)</span>
                <button
                  onClick={() => removeFile(i)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#EF4444",
                    fontWeight: "bold",
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* Message d'erreur */}
          {error && (
            <div
              style={{
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: 6,
                padding: "10px 14px",
                color: "#B91C1C",
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {/* Progression envoi groupé */}
          {sendProgress && (
            <div
              style={{
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                borderRadius: 6,
                padding: "10px 14px",
                color: "#1D4ED8",
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              📨 Envoi en cours... {sendProgress.current}/{sendProgress.total}
            </div>
          )}

          {/* Succès */}
          {success && (
            <div
              style={{
                background: "#F0FDF4",
                border: "1px solid #BBF7D0",
                borderRadius: 6,
                padding: "10px 14px",
                color: "#15803D",
                fontSize: 13,
                marginBottom: 12,
              }}
            >
              ✅ Email envoyé avec succès !
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 20px",
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <button onClick={onClose} style={cancelBtnStyle}>
            Annuler
          </button>
          <button
            onClick={handleSend}
            disabled={sending || success}
            style={{
              ...sendBtnStyle,
              opacity: sending || success ? 0.7 : 1,
              cursor: sending ? "not-allowed" : "pointer",
            }}
          >
            {sending
              ? "Envoi en cours..."
              : `✉️ Envoyer${selectedUsers.length + (to.trim() ? 1 : 0) > 1 ? ` (${selectedUsers.length + to.split(/[,;]/).filter(e => e.trim()).length} destinataires)` : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────
const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  color: "#374151",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  fontSize: 14,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

const cancelBtnStyle = {
  padding: "10px 20px",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  background: "#fff",
  color: "#374151",
  cursor: "pointer",
  fontSize: 14,
};

const sendBtnStyle = {
  padding: "10px 24px",
  border: "none",
  borderRadius: 8,
  background: "#2563EB",
  color: "#fff",
  cursor: "pointer",
  fontSize: 14,
  fontWeight: 600,
};
